import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { post, get, put, del } from '../../utility/fetchWeb';
import notification from '../../utility/notification';

const CategoriesManagement = ({categories, fetchCategories}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = selectedCategory
        ? await put(`/categories/${selectedCategory.id}`, formData)
        : await post('/categories', formData);

      if (response?.code === 200) {
        notification({
          type: 'success',
          message: `Category ${selectedCategory ? 'updated' : 'created'} successfully`
        });
        fetchCategories();
        handleClose();
      }
    } catch (error) {
      notification({
        type: 'error',
        message: `Failed to ${selectedCategory ? 'update' : 'create'} category`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await del(`/categories/${id}`);
      notification({
        type: 'success',
        message: 'Category deleted successfully'
      });
      fetchCategories();
    } catch (error) {
      notification({
        type: 'error',
        message: 'Failed to delete category'
      });
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setSelectedCategory(null);
    setFormData({ name: '', description: '' });
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" component="h1">
          Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsDialogOpen(true)}
        >
          New Category
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <List>
          {categories?.map((category) => (
            <ListItem 
              key={category.id}
              divider
              sx={{
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemText
                primary={category.name}
                secondary={category.description}
                primaryTypographyProps={{
                  fontWeight: 500
                }}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Edit">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setSelectedCategory(category);
                      setFormData({
                        name: category.name,
                        description: category.description
                      });
                      setIsDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(category.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Dialog 
        open={isDialogOpen} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedCategory ? 'Edit Category' : 'New Category'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !formData.name}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Saving...' : selectedCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default CategoriesManagement;